/* Desk notes: which ones the console shows, in what order, and when they end.
 *
 * A desk note is a sticky on the front-desk screen — something staff leave for
 * each other ("Maria is late", "out of towels") or the worker leaves for them.
 * The row component draws them; this file decides *which* and *when*, as plain
 * functions with tests, for the same reason js/rules/checkin-look.js is one: the
 * part that has to be right goes where it can be asserted without a DOM.
 *
 * Rows are `{ id, body, author, source, color, urgent, expiresAt, createdAt,
 * closedAt }` with every time in milliseconds. Nothing here reads the clock;
 * `nowMs` is always passed in, so a test never has to wait for one.
 */

// The four a note may be, in the order the picker offers them.
export const NOTE_COLORS = ['yellow', 'blue', 'green', 'pink'];

// The database refuses past this too. It is also about where a note stops
// fitting on the strip it is drawn on.
export const NOTE_BODY_MAX = 500;

/* The rows to draw: not closed, not expired, not dismissed on this screen.
 *
 * `expiresAt <= nowMs` is expired — a note that ends at 5:00 is gone at 5:00,
 * not a millisecond later. `dismissedIds` is this screen's own set, held so a
 * fetch already in flight cannot bring a dismissed note back for a moment. The
 * dismissal itself is written to the row (closed_at), so it closes the note for
 * every station once it lands. The caller prunes `dismissedIds` and `localPosted`
 * (see loadNotes in store.js); nothing here forgets an id.
 *
 * Urgent first, then newest first within each group. Sorts a copy: the caller's
 * array is the fetched state and is not ours to reorder.
 */
export function visibleNotes(rows, nowMs, dismissedIds) {
  return (rows || []).filter(function (r) {
    return r.closedAt == null && r.expiresAt > nowMs && !dismissedIds.has(r.id);
  }).sort(function (a, b) {
    if (!!a.urgent !== !!b.urgent) return a.urgent ? -1 : 1;
    return b.createdAt - a.createdAt;
  });
}

/* A fetch merged with notes this screen posted a moment ago.
 *
 * A note someone just posted can be missing from a fetch that was already in
 * flight, and without this it would vanish and come back. The fetched copy wins
 * when both exist, because it carries what the server decided (a close, an
 * edit); the local row only stands in until the server has caught up.
 */
export function mergeNotes(fetched, localPosted) {
  var seen = {};
  var out = (fetched || []).map(function (r) {
    seen[r.id] = true;
    return r;
  });
  localPosted.forEach(function (row, id) {
    if (!seen[id]) out.push(row);
  });
  return out;
}

/* When a note posted with this choice ends, in ms, or null if it cannot.
 *
 * 'today' and 'tomorrow' end at closing time, because a note for the day is
 * about the day. The salon may be closed then (closing time null) or it may
 * already be past closing, and a note must not be born expired: those fall back
 * to the end of that day (the next day's, for 'today' after 23:59). 'pick' is whatever the poster chose, and null when
 * that is not in the future — the caller reads null as "ask again", never as
 * "never expires".
 */
export function noteExpiry(choice, t) {
  if (choice === 'today') {
    if (t.closeTodayMs != null && t.closeTodayMs > t.nowMs) return t.closeTodayMs;
    // From 23:59 on, the end of today has already passed; the note would be born
    // expired, so it runs to the end of the next day instead.
    return t.endOfTodayMs > t.nowMs ? t.endOfTodayMs : t.endOfTomorrowMs;
  }
  if (choice === 'tomorrow') {
    return t.closeTomorrowMs != null ? t.closeTomorrowMs : t.endOfTomorrowMs;
  }
  if (choice === 'pick') {
    return t.pickedMs != null && t.pickedMs > t.nowMs ? t.pickedMs : null;
  }
  return null;
}

/* Whether `rows` holds a note this screen has not drawn before, which is what
 * opens a collapsed notes row: a note that arrives must not sit unseen behind
 * it. A note already seen does not count when a fetch brings it back, so a
 * screen waking from sleep stays collapsed if nothing new came in. */
export function hasNewNote(seenIds, rows) {
  return (rows || []).some(function (r) { return !seenIds.has(r.id); });
}

/* Wording for the row, here so it can be asserted without a DOM. */
function cap(s) {
  s = String(s || '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// How a source is spelled on the note; anything else is capitalised as is.
var SOURCE_NAMES = { classpass: 'ClassPass' };

export function sourceName(s) { return SOURCE_NAMES[s] || cap(s); }

// "40 min left", "2 h 5 min left", "3 days left". Whole minutes, rounded up,
// because a note that has 20 seconds left has not ended. Past 48 hours the hours
// stop being useful ("185 h left"), so it counts days.
export function timeLeft(ms) {
  var m = Math.max(1, Math.ceil(ms / 60000));
  if (m < 60) return m + ' min left';
  if (m > 48 * 60) return Math.floor(m / 1440) + ' days left';
  var h = Math.floor(m / 60);
  var r = m % 60;
  return h + ' h' + (r ? ' ' + r + ' min' : '') + ' left';
}
